import React, {RefObject} from "react";
import {Alert, Button, Form, FormInstance, Mentions, Modal, Row, Select, Switch} from "antd";
import RoutePicker from "../../../../shared/pickers/RoutePicker";
import IAction, {
    ACTION_RESERVED_NAMES, ActionResultMessageTypeList, ActionResultMessageTypes,
    ActionType,
    ActionTypes,
    ActionTypesList
} from "../../../../../model/interface/dataStorage/IAction";
import IForm from "../../../../../model/interface/form/IForm";
import {connect, RootStateOrAny} from "react-redux";
import {ISetupState} from "../../../../../redux/reducers/Setup";
import IRoute from "../../../../../model/interface/dataStorage/IRoute";
import Wysiwyg from "../../../../shared/input/Wysiwyg";
import ActionsService from "../../../../../model/service/dataStorage/ActionsService";
import IContentType from "../../../../../model/interface/dataStorage/IContentType";
import Utils from "../../../../../utils";
import SourcesService from "../../../../../model/service/import/SourcesService";
import {API_FILTER_TYPE} from "../../../../../model/constants/ApiConstant";
import IPattern from "../../../../../model/interface/file/generated-document/IPattern";
import PatternsService from "../../../../../model/service/file/generated-document/PatternsService";
import ContentTypeConfiguration from "../ContentTypeConfiguration";
import {update} from "../../../../../redux/actions/Setup";
import ILabelValue from "../../../../../model/interface/util/ILabelValue";
import WorkflowStatesService from "../../../../../model/service/file/WorkflowStatesService";
import GeneratedDocumentsService from "../../../../../model/service/file/generated-document/DocumentsService";
import {RELATION_FIELD_TYPE} from "../../../../../model/interface/dataStorage/IField";
import LocaleText from "../../../settings/dictionary/LocaleText";
import TranslationInput from "../../../../shared/input/TranslationInput";
import FieldPicker from "../../../../shared/pickers/FieldPicker";
import FieldDefaultValuePicker from "../../../../shared/pickers/FieldDefaultValuePicker";
import selectors from "../../../../../redux/selectors";
import {CloseOutlined} from "@ant-design/icons";
import {RouteComponentProps, withRouter} from "react-router-dom";
import Collapse from "../../../../shared/display/collapse/Collapse";
import FieldsService from "../../../../../model/service/dataStorage/FieldsService";
import {IAppState} from "../../../../../redux/store";

interface IProps extends RouteComponentProps{
    resource: IAction,
    onSave: (action: IAction, close: boolean) => void,
    onCancel: () => void,
    forms: IForm[],
    routes: IRoute[],
    contentType: IContentType,
    contentTypes: IContentType[]
    saveSeparately?: boolean,
    update: (changes: any) => void,
    getText: (code: string, fallBack?: string) => string
    store: IAppState
}

interface IState {
    type: ActionType,
    confirmModalEnabled?: boolean
    importSources?: ILabelValue<string, string>[],
    generatedDocumentPatterns?: IPattern[]
    saving?: boolean,
    updateValuesFields: string[]

}

class ContentTypeActionModal extends React.Component<IProps, IState> {


    formRef : RefObject<FormInstance> = React.createRef()

    constructor(props: Readonly<IProps> | IProps) {
        super(props)
        this.state = {
            type: props.resource.type || '',
            updateValuesFields: Object.keys(props.resource.updateValues || {}),
            confirmModalEnabled: props.resource.confirmModalEnabled
        }
    }

    /**
     * Handle form reload after another action is opened
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any) {
        if (prevProps.resource.uuid !== this.props.resource.uuid) {
            this.formRef.current?.resetFields()
            this.setState({
                type: this.props.resource.type || ActionTypes.SCRIPT,
                confirmModalEnabled: this.props.resource.confirmModalEnabled
            }, this.onTypeChange)
        }
    }

    buildTypeOptions() {
        return [...ActionTypesList].filter(type => {
            switch (type.value) {
                case ActionTypes.WORKFLOW_STATE:
                    return this.hasExtension(WorkflowStatesService.EXTENSION_NAME)
                case ActionTypes.GENERATE_DOCUMENT:
                    return this.hasExtension(GeneratedDocumentsService.NAME)
                default:
                    return true
            }
        }).map(t => ({value: t.value, label: <LocaleText code={t.labelFallback || t.label}/>}))
    }

    hasExtension(extension: string) {
        const {contentType} = this.props
        return contentType.extensions.some(e => e.type === extension);
    }

    buildFormOptions() {
        return this.getForms().map((form: IForm) => {
            return {
                value: form.uuid + '',
                label: <LocaleText code={form.label || ''}/>
            }
        })
    }

    buildActionOptions() {
        const {contentType, contentTypes, getText} = this.props
        let actions: ILabelValue[] = []
        contentType.fields.forEach(f => {
            const c = contentTypes.find(c => c.fullClassName === f.targetEntity)
            c?.actions.forEach(action => {
                    let push
                    switch (action.type) {
                        case ActionTypes.FORM:
                        case ActionTypes.SCRIPT:
                        case ActionTypes.DELETE:
                            push = true
                            break
                        case ActionTypes.SHOW:
                            push = f.type === RELATION_FIELD_TYPE.ONE_TO_ONE
                            break
                        default:
                            push = false
                        //TODO what if type is workflowState or other types ??

                    }
                    if (push) {
                        actions.push({
                            value: action.uuid,
                            label: getText(action.label) + ' [' + c.name + ' - ' + (f.name) + ']'
                        })
                    }
                }
            )
        })
        return actions
    }

    onTypeChange = () => {
        const {type} = this.state
        const {contentType} = this.props
        if (type === ActionTypes.IMPORT) {
            SourcesService.choiceList('name-reader', {
                filters: {
                    0: {
                        field: 'type',
                        type: API_FILTER_TYPE.EQUAL,
                        value: contentType.fullClassName
                    },
                    1: {
                        field: 'parent',
                        type: API_FILTER_TYPE.IS_NULL
                    }
                }
            }).then(({results}) => {
                this.setState({
                    importSources: Object.entries(results).map(([value, label]) => ({value, label}))
                }, () => this.formRef.current?.resetFields(['importSource']))
            })
        }
        if (type === ActionTypes.GENERATE_DOCUMENT) {
            PatternsService.collectionList({
                filters: {
                    0: {
                        field: 'contentType',
                        type: API_FILTER_TYPE.EQUAL,
                        value: contentType.uuid
                    }
                }
            }).then(({results}) => {
                this.setState({generatedDocumentPatterns: results},
                    () => this.formRef.current?.resetFields(['generatedDocumentPattern']))
            })
        }
    }

    change(values: any) {
        if (values.type) {
            this.setState({type: values.type}, this.onTypeChange)
        }

        if (values.confirmModalEnabled !== undefined) {
            this.setState({confirmModalEnabled: values.confirmModalEnabled})
        }
    }

    submit = () => {
        const {saveSeparately, resource, onSave, contentType, contentTypes, update} = this.props
        this.formRef.current?.validateFields().then(values => {
            Object.entries(values.updateValues || {}).forEach(([key, value]) => {
                if (!value){
                    values.updateValues[key] = null
                }
            })
            const action = {...resource, ...values}
            if (saveSeparately) {
                this.setState({saving: true})
                if (action.id) {
                    return ActionsService.resourceUpdate(action?.id, ContentTypeConfiguration.mapAction(action))
                        .then(() => onSave(action, true))
                }
                return ActionsService.collectionCreate(ContentTypeConfiguration.mapAction(action))
                    .then(() => {
                        update({
                            contentTypes: [...contentTypes.filter(c => c.uuid !== contentType.uuid), {
                                ...contentType,
                                actions: [...contentType.actions.filter(a => a.uuid !== action.uuid), action]
                            }]
                        })
                        onSave(action, true)
                    })
            }
            onSave(action, true)
        })
    }

    showConfirmModal = () => {
        ActionsService.showConfirmModal(this.formRef.current?.getFieldValue('confirmModalText'))
            .then().catch(() => console.error('rejected'))
    }

    getForms(): IForm[] {
        const {forms, contentType} = this.props
        return forms.filter(form => form.contentType === contentType.uuid)
    }

    addUpdateValuesField = (field: string) => {
        this.setState(state => ({updateValuesFields: [...state.updateValuesFields, field]}))
    }

    removeUpdateValuesField = (field: string) => {
        this.setState(state => ({updateValuesFields: [...state.updateValuesFields.filter(f => f !== field)]}))
    }

    render() {
        const {resource, onCancel, contentType, history, match, store} = this.props
        const {type, confirmModalEnabled, importSources, generatedDocumentPatterns, saving, updateValuesFields} = this.state

        return (
            <Modal width={800} title={resource.label ? <LocaleText code={resource.label}/> : 'Nová akce'} visible={!!resource} onCancel={onCancel}
                   onOk={this.submit} destroyOnClose={true} closable={true}
                   footer={[
                       <Button key="submit" type="primary" loading={saving} onClick={this.submit}>
                           <LocaleText code={'save'} fallback={'Uložit'}/>
                       </Button>
                   ]}
            >
                <Form initialValues={{...resource, confirmModalText: 'Opravdu chcete provést tuto akci?'}} ref={this.formRef}
                      onValuesChange={(values) => this.change(values)}>
                    <Form.Item label={'Titulek'} name={'label'} rules={[{required: true, message: <LocaleText code={'form.item.required'}/>}]}>
                        <TranslationInput uuid={resource.uuid} code={'configuration.action.' + resource.uuid}/>
                    </Form.Item>
                    <Form.Item
                        label={'Strojový název'}
                        name={'name'}
                        rules={[
                            () => ({
                                validator: (_: any, name: string) => {
                                    const index = Utils.findIndex(this.props.contentType.actions, {name})
                                    if (index < 0 || this.props.contentType.actions[index].uuid === this.props.resource!.uuid) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Název musí být u typu obsahu unikátní'));
                                }
                            })
                        ]}
                    >
                        <Mentions
                            split={''}
                            readOnly={resource ? resource.locked : false}
                            placeholder={"název nebo # pro vyhrazený název"}
                            prefix={'#'}
                        >
                            {Object.entries(ACTION_RESERVED_NAMES).map(([label, value]) => (
                                <Mentions.Option key={value} value={value.slice(1)}>
                                    {label}
                                </Mentions.Option>
                            ))}
                        </Mentions>
                    </Form.Item>
                    <Form.Item
                        label={'Typ'}
                        name={'type'}
                    >
                        <Select options={this.buildTypeOptions()}/>
                    </Form.Item>
                    {[ActionTypes.FORM, ActionTypes.SCRIPT_FORM].indexOf(type) >= 0 && (
                        <Form.Item
                            label={'Formuláře'}
                            name={'forms'}
                        >
                            <Select mode={"multiple"} options={this.buildFormOptions()}/>
                        </Form.Item>
                    )}
                    {[ActionTypes.RELATION_ACTION].indexOf(type) >= 0 && (
                        <>
                            <Form.Item
                                label={'Vztahová akce'}
                                name={'relationAction'}
                            >
                                <Select options={this.buildActionOptions()}/>
                            </Form.Item>
                            <Form.Item
                                label={<LocaleText code={'configuration.action.relation-action.create-new'} fallback={'Vytvořit nový záznam pro vztahovú akci'} />}
                                name={'relationActionCreateNew'} valuePropName={'checked'}
                            >
                                <Switch/>
                            </Form.Item>
                        </>
                    )}
                    {[ActionTypes.IMPORT].indexOf(type) >= 0 && (
                        <Form.Item
                            label={'Zdroj'}
                            name={'importSource'}
                        >
                            <Select allowClear={true} loading={!importSources}>
                                {importSources?.map(source => (
                                    <Select.Option value={source.value}>
                                        <LocaleText code={source.label}/>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {[ActionTypes.GENERATE_DOCUMENT].indexOf(type) >= 0 && (
                        <Form.Item
                            label={'Vzor'}
                            name={'generatedDocumentPattern'}
                        >
                            <Select allowClear={true} loading={!generatedDocumentPatterns}>
                                {generatedDocumentPatterns?.map(pattern => (
                                    <Select.Option value={pattern.uuid}>
                                        {pattern.label} [{pattern.file.fullName}]
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {[ActionTypes.FIELD_CHANGE].includes(type) && (
                        <Form.Item
                            label={"Pole"}
                            name={"changeFields"}
                        >
                            <FieldPicker multiple={true} serviceClassName={contentType.fullClassName} output={'uuid'}/>
                        </Form.Item>
                    )}
                    {[
                        ActionTypes.FORM,
                        ActionTypes.SCRIPT_FORM,
                        ActionTypes.SHOW,
                        ActionTypes.IMPORT,
                        ActionTypes.REVISION_HISTORY
                    ].indexOf(type) >= 0 && (
                        <Form.Item
                            label={'URL'}
                            name={'route'}
                            rules={[{
                                required: [
                                    ActionTypes.IMPORT, ActionTypes.REVISION_HISTORY, ActionTypes.SHOW, ActionTypes.FORM
                                ].indexOf(type) >= 0
                            }]}>
                            <RoutePicker/>
                        </Form.Item>
                    )}
                    {![ActionTypes.RELATION_ACTION].includes(type) && (
                        <Form.Item
                            label={"Přesměrování"}
                            name={"afterRedirect"}
                        >
                            <RoutePicker/>
                            {/*<Select options={routeOptions} />*/}
                        </Form.Item>
                    )}
                    {![ActionTypes.DELETE].includes(type) && (
                        <Form.Item label={'Hodnoty'}>
                            <FieldPicker autoReset={true} className={'mb-2'} mode={'tree'} serviceClassName={contentType.fullClassName} output={'name'}
                                         onChange={field => this.addUpdateValuesField(field as string)}/>
                            {updateValuesFields.map((field) => {
                                const fieldObject = FieldsService.getFieldFromRelationChain(field, contentType, store)
                                return fieldObject ? (
                                    <Row className={'w-100'} wrap={false} key={fieldObject.uuid}>
                                        <Form.Item label={<LocaleText code={fieldObject.label || fieldObject.name}/>} className={'w-100 mb-2'}
                                                   name={['updateValues', field]}>
                                            <FieldDefaultValuePicker className={'w-100'} match={match} history={history}
                                                                     field={fieldObject} originalContentTypeClassName={contentType.fullClassName}/>
                                        </Form.Item>
                                        <Button type={'link'} danger={true} icon={<CloseOutlined/>}
                                                onClick={() => this.removeUpdateValuesField(field)}/>
                                    </Row>
                                ) : ''
                            })}
                        </Form.Item>
                    )}
                    <Form.Item valuePropName={'checked'} label={'Provádět v modalu'} name={'modal'}>
                        <Switch disabled={![
                            ActionTypes.FORM, ActionTypes.SHOW, ActionTypes.IMPORT, ActionTypes.REVISION_HISTORY, ActionTypes.RELATION_ACTION
                        ].includes(type)}/>
                    </Form.Item>
                    <Collapse title={<LocaleText code={'configuration.action.more-settings'} fallback={'Další nastavení'}/>}>
                        <Form.Item valuePropName={'checked'} label={'Zobrazit dialog pro potvrzení'}
                                   name={'confirmModalEnabled'}>
                            <Switch/>
                        </Form.Item>
                        {confirmModalEnabled && (
                            <div>
                                <Form.Item name={'confirmModalText'} rules={[{required: true}]}>
                                    <TranslationInput code={'configuration.action.confirm-modal.' + resource.uuid} preserveCode={true} customInput={(value, triggerSave, onChange, className) =>
                                        <Wysiwyg mode={"medium"} onSubmit={triggerSave}
                                                 value={value}
                                                 onChange={onChange}
                                                 placeholder={'Obsah potvrzovacího dialogu'}
                                                 className={className}/>}/>
                                </Form.Item>
                                <Button onClick={this.showConfirmModal}><LocaleText code={'configuration.action.preview-confirm-modal'} fallback={'Zobrazit dialog'}/></Button>
                            </div>
                        )}
                        {[
                            ActionTypes.FORM,
                            ActionTypes.SCRIPT_FORM,
                            ActionTypes.SHOW,
                            ActionTypes.IMPORT,
                            ActionTypes.REVISION_HISTORY,
                            ActionTypes.RELATION_ACTION,
                            ActionTypes.WORKFLOW_STATE,
                            ActionTypes.SCRIPT
                        ].indexOf(type) >= 0 && (
                            <Form.Item valuePropName={'checked'} label={<LocaleText code={'configuration.action.no-context-reload'} fallback={'Žádné opětovné načtení kontextu'}/>}
                                       name={'noContextReload'}>
                                <Switch/>
                            </Form.Item>
                        )}
                        <Form.Item label={'Zpráva o výsledku'} name={'resultMessage'} labelCol={{ span: 24 }}>
                            <Wysiwyg mode={'medium'} placeholder={'Akce úspěšně provedena'}/>
                        </Form.Item>
                        <Form.Item label={'Typ zprávy o výsledku'} name={'resultMessageType'} initialValue={ActionResultMessageTypes.SUCCESS}>
                            <Select>
                                {ActionResultMessageTypeList.map(t => (
                                    <Select.Option value={t.value} key={t.value}>
                                        <Row justify={"space-between"}>
                                            <LocaleText code={t.label} fallback={t.labelFallback}/>
                                            {
                                                {
                                                    [ActionResultMessageTypes.SUCCESS]:  <Alert message="" type="success" showIcon />,
                                                    [ActionResultMessageTypes.ERROR]: <Alert message="" type="error" showIcon />,
                                                    [ActionResultMessageTypes.NOTIFICATION]:  <Alert message="" type="info" showIcon />,
                                                    [ActionResultMessageTypes.WARNING]: <Alert message="" type="warning" showIcon />
                                                }[t.value]
                                            }
                                        </Row>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Collapse>
                </Form>
            </Modal>
        )
    }


}

const mapStateToProps = (state: IAppState) => {
    const {contentTypes, routes} = state.setup as ISetupState
    return {
        store: state,
        contentTypes,
        routes,
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        update: (changes: any) => dispatch(update(changes))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ContentTypeActionModal))
